import React, { useEffect, useRef, useState } from 'react';
import { Code2, CheckCircle2 } from 'lucide-react';
import { projects } from '../data/mock';

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section id="projects" ref={sectionRef} className="py-20 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Featured <span className="text-cyan-400">Projects</span>
          </h2>
          <div className="w-20 h-1 bg-cyan-400 mx-auto mb-12"></div>

          <div className="grid gap-8">
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-slate-900 rounded-xl overflow-hidden shadow-xl border border-slate-800 hover:border-cyan-400 transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="p-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-cyan-500/10 p-3 rounded-lg">
                        <Code2 className="text-cyan-400" size={32} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">{project.title}</h3>
                        <p className="text-gray-400 mb-3">{project.description}</p>
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="text-sm text-gray-400">Role:</span>
                          <span className="text-cyan-400 font-semibold">{project.role}</span>
                          <span className="mx-2 text-gray-600">|</span>
                          <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full font-semibold">
                            {project.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-3">Technologies Used</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, idx) => (
                        <span
                          key={idx}
                          className="px-4 py-2 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700 hover:border-cyan-400 transition-colors duration-300 font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Key Features</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {project.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="text-cyan-400 flex-shrink-0 mt-0.5" size={20} />
                          <span className="text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
